import { useLayoutEffect } from "react"
import PropTypes from "prop-types"
import { Paper, Box, Button } from "@mui/material"
import { map, get } from "lodash"
import Header from "components/assets-header"
import NoData from "components/intraday/no-data"
import { NORMALIZATION } from "helpers/segments"
import { DATE, FILTER_ON_GROUP, SEARCHABLE_MENU_ITEM, FILTER } from "helpers/shapes"
import IntradayAssetTable from "modules/intraday-asset-table"
import IntradayRiskFactors from "modules/intraday-risk-factors"
import SearchableMenu from "components/searchable-menu"
import IntradayRebalanceReload from "modules/intraday-rebalance-reload"
import { useNavigate } from "react-router-dom"
import { useCheckLastPreferenceRedirect } from "hooks/preferences"

export default function Intraday({
  hasData,
  isRefreshing,
  lastRefresh,
  availableRollups,
  grouping,
  setGrouping,
  isExpanded,
  setExpansionMode,
  filtersVisible,
  toggleFilters,
  account,
  accountId,
  date,
  filter,
  filterOns,
  modelId,
  normalization,
  portfolio,
  portfolioId,
  setNormalization,
  segment,
  selectedScreen,
  selectScreen,
  menuItems,
  intradayEnabled,
  currency,
  updateFilters,
  addedFilters,
}) {
  const navigate = useNavigate()
  const { wasRedirectedFromPreferences, deletePreferenceAndRedirectToDefault } = useCheckLastPreferenceRedirect()

  useLayoutEffect(() => {
    if (!intradayEnabled) {
      if (wasRedirectedFromPreferences) {
        deletePreferenceAndRedirectToDefault()
      } else {
        navigate(`/${account}/p/${portfolio}/analyze`, { replace: true })
      }
    }
  }, [account, navigate, portfolio, intradayEnabled, wasRedirectedFromPreferences, deletePreferenceAndRedirectToDefault])

  const handleSelectScreen = screen => {
    selectScreen(get(screen, "id"))
  }

  const customSelectScreen = <Paper square className="screen-menu-container">
    <Box className="screen-menu-container">
      <SearchableMenu items={menuItems} current={selectedScreen} onSelect={handleSelectScreen} hideSearchBox className="intraday-select-screen" />
    </Box>
  </Paper>

  const header = <Header {...{
    hasData,
    isRefreshing,
    lastRefresh,
    availableRollups,
    ...(selectedScreen === "assets" && {
      setGrouping,
      isExpanded,
      setExpansionMode,
      currentGrouping: grouping,
    }),
    filtersVisible,
    toggleFilters,
    account,
    date,
    filter,
    filterOns,
    modelId,
    normalization,
    assetId: portfolio,
    assetType: "PORTFOLIO",
    setNormalization,
    isIntraday: true,
    title: "Intraday",
    selectedScreen,
    customSelectScreen,
    isActive: false,
    showBenchmarkNote: false,
    currency,
    updateFilters,
    addedFilters,
    customLeftButtons: [
      <IntradayRebalanceReload key="intraday-rebalance-reload" destination={`/${account}/p/${portfolio}/rebalance/intraday`} type="asset-review">
        <Button className="intraday-simulator-button" variant="contained" color="primary" size="small">Rebalance Positions</Button>
      </IntradayRebalanceReload>,
    ],
  }}
  />

  let main = <Paper className="details"><NoData autoRefresh /></Paper>
  if (hasData) {
    if (selectedScreen === "assets") {
      main = <Paper className="details">
        <IntradayAssetTable {...{
          account,
          accountId,
          portfolio,
          portfolioId,
          modelId,
          date,
          segment,
          currentGrouping: grouping,
          isExpanded,
          lastRefresh,
        }}
        />
      </Paper>
    } else if (selectedScreen === "factors") {
      main = <IntradayRiskFactors {...{
        account,
        accountId,
        portfolio,
        portfolioId,
        modelId,
        date,
        segment,
        filtersVisible,
      }}
      />
    }
  }
  return <div className="modules_intraday">
    {header}
    {main}
  </div>
}

Intraday.propTypes = {
  hasData: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  idSort: PropTypes.string,
  isRefreshing: PropTypes.bool.isRequired,
  lastRefresh: PropTypes.string,
  availableRollups: PropTypes.array.isRequired,
  grouping: PropTypes.object,
  setGrouping: PropTypes.func.isRequired,
  setExpansionMode: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  filtersVisible: PropTypes.bool.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  account: PropTypes.string.isRequired,
  accountId: PropTypes.string.isRequired,
  date: DATE,
  filter: PropTypes.object,
  filterOns: PropTypes.arrayOf(FILTER_ON_GROUP).isRequired,
  modelId: PropTypes.string.isRequired,
  portfolio: PropTypes.string.isRequired,
  portfolioId: PropTypes.string.isRequired,
  normalization: PropTypes.oneOf(map(NORMALIZATION)),
  setNormalization: PropTypes.func.isRequired,
  segment: PropTypes.object,
  selectedScreen: PropTypes.string.isRequired,
  selectScreen: PropTypes.func.isRequired,
  intradayEnabled: PropTypes.bool,
  menuItems: PropTypes.arrayOf(SEARCHABLE_MENU_ITEM).isRequired,
  currency: PropTypes.string,
  updateFilters: PropTypes.func.isRequired,
  addedFilters: PropTypes.arrayOf(FILTER),
}
